import React, { useRef, useState } from 'react';
import { Mail, Loader2, X, Send } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';
import { EXPERIENCE_DATA, RIG_DATA } from '@/components/booking/bookingDefaults';
import { fmtDate } from '@/lib/utils';
import MacroPicker from '@/components/admin/crm/MacroPicker';
import { insertMacroInto, restoreCaret } from '@/lib/crmMacros';

function expName(key) {
  return EXPERIENCE_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—');
}
function rigLabel(key) {
  return RIG_DATA[key]?.name || (key ? key.replace(/_/g, ' ').toUpperCase() : '—');
}

const TEMPLATES = [
  { id: 'blank', label: 'Blank message' },
  { id: 'reschedule', label: 'Reschedule Session' },
];

function buildTemplate(id, booking) {
  const firstName = booking.customer_name?.split(' ')[0] || 'there';
  const originalDateTime = `${fmtDate(booking.date) || '—'} at ${booking.time_slot || '—'}`;
  if (id === 'reschedule') {
    return {
      subject: 'Important update regarding your booking at Motion Reality',
      message: `Hi ${firstName},\n\nThank you for booking with us.\n\nDue to [brief reason, e.g. scheduled rig maintenance / venue preparation / a scheduling conflict], we need to reschedule your session originally booked for ${originalDateTime}. We sincerely apologise for any inconvenience this causes to your plans.\n\nWe would love to move your session to [Proposed Date & Time].\n\nIf this time works for you: Simply reply to this email, and we will update your booking immediately.\n\nIf you need an alternative slot: Let us know what date and time suits your schedule best, or give our desk a call on 0151 528 3001 and we will sort it out for you.\n\n[Optional: To make up for the disruption, we'll also add extra track time / a complimentary voucher to your session.]\n\nThanks for your understanding,\n\nThe Motion Reality Team\n\n---\nBooking ref: ${booking.id}\nExperience: ${expName(booking.experience_type)}\nRig: ${rigLabel(booking.rig_type)}`,
    };
  }
  return {
    subject: `Your ${expName(booking.experience_type)} session on ${fmtDate(booking.date) || '—'}`,
    message: `Hi ${firstName},\n\n\n\n— The Motion Reality Team\n\n---\nBooking ref: ${booking.id}\nExperience: ${expName(booking.experience_type)}\nRig: ${rigLabel(booking.rig_type)}\nDate: ${fmtDate(booking.date) || '—'}\nTime: ${booking.time_slot || '—'}\nPaid: ${booking.total_price != null ? `£${Number(booking.total_price).toFixed(2)}` : '—'}`,
  };
}

export default function BookingEmailComposer({ booking, onClose }) {
  const { toast } = useToast();
  const [template, setTemplate] = useState('blank');
  const initial = buildTemplate('blank', booking);
  const [subject, setSubject] = useState(initial.subject);
  const [message, setMessage] = useState(initial.message);
  const [sending, setSending] = useState(false);
  const messageRef = useRef(null);

  const insertMacro = (text, macro) => {
    const { next, caret } = insertMacroInto(messageRef.current, text);
    setMessage(next);
    restoreCaret(messageRef.current, caret);
    if (macro.subject && !subject.trim()) setSubject(macro.subject);
  };

  const applyTemplate = (id) => {
    setTemplate(id);
    const t = buildTemplate(id, booking);
    setSubject(t.subject);
    setMessage(t.message);
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      const res = await base44.functions.invoke('sendBookingEmail', {
        to: booking.customer_email,
        subject: subject.trim(),
        message: message.trim(),
      });
      if (res.data?.success) {
        toast({ title: 'Email sent', description: `Delivered to ${booking.customer_email}` });
        onClose();
      } else {
        throw new Error(res.data?.error || 'Send failed');
      }
    } catch (e) {
      toast({ title: 'Could not send email', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-primary/40 bg-[#161616] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">EMAIL CUSTOMER</p>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">TO</p>
        <p className="text-white/60 text-xs break-all">{booking.customer_email || '—'}</p>
      </div>

      <div>
        <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1">TEMPLATE</label>
        <select
          value={template}
          onChange={(e) => applyTemplate(e.target.value)}
          className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-primary/60"
        >
          {TEMPLATES.map((t) => (
            <option key={t.id} value={t.id} className="bg-[#0d0d0f]">{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1">SUBJECT</label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-primary/60"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-white/30 text-[10px] font-bold tracking-widest">MESSAGE</label>
        <MacroPicker
          channel="email"
          context={{ customer_name: booking.customer_name, booking_ref: booking.id }}
          onInsert={insertMacro}
        />
        <textarea
          ref={messageRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={9}
          className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm px-3 py-2 resize-y focus:outline-none focus:border-primary/60"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-4 py-2 transition-colors"
        >
          CANCEL
        </button>
        <button
          onClick={handleSend}
          disabled={sending || !subject.trim() || !message.trim()}
          className="bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-5 py-2.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sending ? 'SENDING…' : 'SEND EMAIL'}
        </button>
      </div>
    </div>
  );
}